import { EngineError } from "../types";

export interface ContextInfo {
  gl: WebGL2RenderingContext;
  /** RGBA16F is color-renderable (EXT_color_buffer_float or _half_float). */
  floatTrails: boolean;
  maxTextureSize: number;
  maxPointSize: number;
  dispose(): void;
}

export interface ContextHooks {
  onLost(): void;
  onRestored(): void;
}

/**
 * Acquire WebGL2 with the attributes the pipeline depends on
 * (no default-framebuffer readbacks, so preserveDrawingBuffer stays false)
 * and wire context-loss events. Throws EngineError("webgl2-unsupported").
 */
export function acquireContext(
  canvas: HTMLCanvasElement,
  hooks: ContextHooks,
): ContextInfo {
  const gl = canvas.getContext("webgl2", {
    alpha: false,
    antialias: false,
    depth: false,
    stencil: false,
    preserveDrawingBuffer: false,
    powerPreference: "high-performance",
  });
  if (!gl) {
    throw new EngineError(
      "webgl2-unsupported",
      "WebGL2 is not available on this device/browser",
    );
  }

  const floatTrails = Boolean(
    gl.getExtension("EXT_color_buffer_float") ??
      gl.getExtension("EXT_color_buffer_half_float"),
  );
  const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
  const pointRange = gl.getParameter(
    gl.ALIASED_POINT_SIZE_RANGE,
  ) as Float32Array;
  const maxPointSize = pointRange[1] ?? 64;

  const onLost = (e: Event) => {
    e.preventDefault();
    hooks.onLost();
  };
  const onRestored = () => hooks.onRestored();
  canvas.addEventListener("webglcontextlost", onLost);
  canvas.addEventListener("webglcontextrestored", onRestored);

  // Fixed pipeline state: our dither is explicit in the present shader.
  gl.disable(gl.DITHER);
  gl.disable(gl.DEPTH_TEST);

  return {
    gl,
    floatTrails,
    maxTextureSize,
    maxPointSize,
    dispose() {
      canvas.removeEventListener("webglcontextlost", onLost);
      canvas.removeEventListener("webglcontextrestored", onRestored);
    },
  };
}
